import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { getRouterSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, from, map, of, switchMap } from 'rxjs';
import { DealerFeature, ImprintPageFeature, TrackingFeature } from '../../..';
import { VehicleApiService } from '../../api/vehicle-api.service';
import { isHWS, PLATFORM_TOKEN } from '../../providers/platform.token';
import { vehicleActions } from './vehicle.actions';
import { vehicleState } from './vehicle.reducer';

@Injectable()
export class VehicleEffects {
    actions$ = inject(Actions);
    vehicleApi = inject(VehicleApiService);
    store = inject(Store);
    activatedRoute = inject(ActivatedRoute);
    platformToken = inject(PLATFORM_TOKEN);
    isHWS = isHWS();

    initVehicleMarket = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.init),
            switchMap(() => {
                return [vehicleActions.loadNumberOfVehicles(), vehicleActions.loadVehicleList()];
            }),
        );
    });

    loadNumberOfVehicles = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadNumberOfVehicles),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectFilterSelected),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getNumberOfVehicles({
                        ...filter,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                    }),
                ).pipe(
                    switchMap((numberOfVehicles) => {
                        return [vehicleActions.loadNumberOfVehiclesSuccess(numberOfVehicles)];
                    }),
                    catchError((error) => of(vehicleActions.loadNumberOfVehiclesFailure({ error }))),
                );
            }),
        );
    });

    loadFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadFilter),
            concatLatestFrom(() => [this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null)]),
            switchMap(([_, dealer]) => {
                const dealerId = dealer ? dealer.id : undefined;

                return from(this.vehicleApi.getFilter(dealerId)).pipe(
                    switchMap((filterInputs) => {
                        return [vehicleActions.loadFilterSuccess({ filterInputs })];
                    }),
                    catchError((error) => of(vehicleActions.loadFilterFailure({ error }))),
                );
            }),
        );
    });

    loadVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadVehicleList),
            concatLatestFrom(() => [
                this.store.select(getRouterSelectors().selectQueryParam('page')),
                this.store.select(vehicleState.selectFilterSelected),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),

            switchMap(([_, page, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getVehicleList({
                        page: page ? Number(page) : 1,
                        limit: 12,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                        ...filter,
                    }),
                ).pipe(
                    switchMap((list) => {
                        return [vehicleActions.loadVehicleListSuccess({ list })];
                    }),
                    catchError((error) => of(vehicleActions.loadVehicleListFailure({ error }))),
                );
            }),
        );
    });

    loadDetailVehicle$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadVehicleDetail),
            switchMap(({ slug }) => {
                if (!slug) throw new Error('Slug is not defined');

                return from(this.vehicleApi.getVehicleDetail(slug)).pipe(
                    switchMap((vehicle) => {
                        return [
                            vehicleActions.loadVehicleDetailSuccess({ vehicle }),
                            ImprintPageFeature.actions.loadDealerImprintOverlayBySlug({ slug: vehicle.dealer.slug }),
                            vehicleActions.loadSimilarVehicleList(),
                        ];
                    }),
                    catchError((error) => of(vehicleActions.loadVehicleDetailFailure({ error }))),
                );
            }),
        );
    });

    loadSimilarVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadSimilarVehicleList),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectLoadedDetail),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, vehicle, dealer]) => {
                const price = vehicle.price.buy;
                let year = vehicle.modelyear;
                let mileage = vehicle.details.mileage;
                if (year < 1900) {
                    year = new Date().getFullYear() - 1;
                }
                if (mileage - 5000 < 0) {
                    mileage = 5000;
                }
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getVehicleList(
                        {
                            page: 1,
                            limit: 7,
                            ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                            model: vehicle.model ? [vehicle.model] : undefined,
                            price: { min: Math.round(price * 0.8), max: Math.round(price * 1.2) },
                            firstRegistrationYear: { min: year - 1, max: year + 1 },
                            mileage: mileage ? { min: mileage - 5000, max: mileage + 5000 } : undefined,
                        },
                        vehicle.id,
                    ),
                ).pipe(
                    switchMap((list) => {
                        return [vehicleActions.loadSimilarVehicleListSuccess({ list })];
                    }),
                    catchError((error) => of(vehicleActions.loadSimilarVehicleListFailure({ error }))),
                );
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnReset = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.resetFilter),
            concatLatestFrom(() => [
                this.store
                    .select(vehicleState.selectTrackingKeys)
                    .pipe(map((keys) => Object.keys(keys).filter((key) => keys[key] === true))),
                this.store
                    .select(vehicleState.selectTrackingKeysPreview)
                    .pipe(map((keys) => Object.keys(keys).filter((key) => keys[key] === true))),
            ]),
            switchMap(([_, trackingKeys, trackingKeysPreview]) => {
                return [
                    vehicleActions.loadFilterPreviewNumberOfVehicles(),
                    ...[...trackingKeys, ...trackingKeysPreview].map((key) =>
                        TrackingFeature.actions.filterTracking({ filterType: key }),
                    ),
                    vehicleActions.resetTrackingKeys(),
                ];
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnAdd = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addFilter),
            debounceTime(1000),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerTrackingOnAddFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.addFilter),
            switchMap(({ trackingKeys }) => {
                return trackingKeys.map((key) => TrackingFeature.actions.filterTracking({ filterType: key }));
            }),
        );
    });

    triggerNumberOfVehiclesOnResetPreviewFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.resetPreviewFilter),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerLoadPreviewNumberOfVehiclesOnRemove = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.removeFilter),
            debounceTime(1000),
            switchMap(() => {
                return [vehicleActions.loadFilterPreviewNumberOfVehicles()];
            }),
        );
    });

    triggerTrackingOnRemoveFilter = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.removeFilter),
            switchMap(({ trackingKeys }) => {
                return trackingKeys.map((filterType) => TrackingFeature.actions.filterTracking({ filterType }));
            }),
        );
    });

    loadPreviewNumberOfVehicles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(vehicleActions.loadFilterPreviewNumberOfVehicles),
            concatLatestFrom(() => [
                this.store.select(vehicleState.selectFilterSelectedPreview),
                this.isHWS ? this.store.select(DealerFeature.state.selectLoadedDealer) : of(null),
            ]),
            switchMap(([_, filter, dealer]) => {
                const subdealerIds = dealer ? dealer.subDealers.map((subDealer) => subDealer.id) : [];
                return from(
                    this.vehicleApi.getNumberOfVehicles({
                        ...filter,
                        ...(dealer ? { dealerId: [dealer.id, ...subdealerIds].join(',') } : {}),
                    }),
                ).pipe(
                    switchMap((numberOfVehicles) => {
                        return [vehicleActions.loadFilterPreviewNumberOfVehiclesSuccess(numberOfVehicles)];
                    }),
                    catchError((error) => of(vehicleActions.loadFilterPreviewNumberOfVehiclesFailure({ error }))),
                );
            }),
        );
    });
}
